import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Alert,
  AlertTitle
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  AccountBalance as FundsIcon,
  TrendingUp as PositionsIcon,
  Person as AccountsIcon,
  Assessment as ReportsIcon,
  Delete as DeleteIcon,
  Badge as IdentifierIcon
} from '@mui/icons-material'
import { useAuth } from '../../auth/useAuth'
import { useNavigate } from 'react-router-dom'

// Import module components
import FundsManagement from './components/FundsManagement/FundsManagement'
import PositionsManagement from './components/PositionsManagement/PositionsManagement'
import AccountsManagement from './components/AccountsManagement/AccountsManagement'
import ReportsManagement from './components/ReportsManagement/ReportsManagement'
import DeletedItemsManagement from './components/DeletedItemsManagement/DeletedItemsManagement'
import IdentifiersManagement from './components/IdentifiersManagement/IdentifiersManagement'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`strategic-tabpanel-${index}`}
      aria-labelledby={`strategic-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `strategic-tab-${index}`,
    'aria-controls': `strategic-tabpanel-${index}`,
  }
}

const StrategicFundManagement: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [tabValue, setTabValue] = useState(0)

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      await logout()
      handleClose()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleBackToPortal = () => {
    navigate('/')
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#1a237e' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackToPortal}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Strategic Fund Management
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.name || user?.username || 'User'}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {(user?.name || user?.username || 'U').charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={() => {
                handleClose()
                // Profile dialog would go here
              }}>
                <AccountCircleIcon sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Welcome Section */}
        <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1a237e 0%, #3f51b5 100%)', color: 'white' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Strategic Fund Management System
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Complete fund management solution with portfolio tracking, position management, and comprehensive reporting
          </Typography>
        </Paper>


        {/* Navigation Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="strategic fund management tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          >
            <Tab
              icon={<FundsIcon />}
              label="Funds Management"
              {...a11yProps(0)}
              sx={{ minWidth: 160 }}
            />
            <Tab
              icon={<PositionsIcon />}
              label="Positions Management"
              {...a11yProps(1)}
              sx={{ minWidth: 180 }}
            />
            <Tab
              icon={<AccountsIcon />}
              label="Accounts Management"
              {...a11yProps(2)}
              sx={{ minWidth: 180 }}
            />
            <Tab
              icon={<IdentifierIcon />}
              label="Identifiers"
              {...a11yProps(3)}
              sx={{ minWidth: 120 }}
            />
            <Tab
              icon={<ReportsIcon />}
              label="Reports & Analytics"
              {...a11yProps(4)}
              sx={{ minWidth: 170 }}
            />
            <Tab
              icon={<DeleteIcon />}
              label="Deleted Items"
              {...a11yProps(5)}
              sx={{ minWidth: 140 }}
            />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <FundsManagement />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <PositionsManagement />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <AccountsManagement />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <IdentifiersManagement />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <ReportsManagement />
        </TabPanel>
        <TabPanel value={tabValue} index={5}>
          <DeletedItemsManagement />
        </TabPanel>
      </Container>
    </Box>
  )
}

export default StrategicFundManagement
